import { getSpritePreset } from "../../data/sprites";
import type { CardDraft, SceneLayerSlot } from "../../shared/game";
import { getSceneLayer, SCENE_SCALE_STEPS } from "../../lib/card-scene";

type SceneLayerControlsProps = {
  card: CardDraft;
  activeSlot: SceneLayerSlot;
  onSelectSlot: (slot: SceneLayerSlot) => void;
  onMoveLayer: (slot: SceneLayerSlot, dx: number, dy: number) => void;
  onScaleLayer: (slot: SceneLayerSlot, delta: number) => void;
  onClearLayer: (slot: SceneLayerSlot) => void;
  disabled?: boolean;
};

const SLOT_LABELS: Record<SceneLayerSlot, string> = {
  background: "Background",
  character: "Character",
  effect: "Effect",
};

export function SceneLayerControls({
  card,
  activeSlot,
  onSelectSlot,
  onMoveLayer,
  onScaleLayer,
  onClearLayer,
  disabled = false,
}: SceneLayerControlsProps) {
  const activeLayer = getSceneLayer(card, activeSlot);
  const activeSprite = getSpritePreset(activeLayer.spriteId);
  const canAdjust = !disabled && Boolean(activeLayer.spriteId);
  const minScale = SCENE_SCALE_STEPS[0];
  const maxScale = SCENE_SCALE_STEPS[SCENE_SCALE_STEPS.length - 1];

  return (
    <div className="control-stack">
      <div className="control-section">
        <div className="control-label-row">
          <span className="control-label">Layer Slot</span>
          <span className="control-helper">{activeSprite?.name ?? "No sprite assigned"}</span>
        </div>
        <div className="slot-button-row">
          {(["background", "character", "effect"] as SceneLayerSlot[]).map((slot) => (
            <button
              key={slot}
              type="button"
              disabled={disabled}
              onClick={() => onSelectSlot(slot)}
              className={activeSlot === slot ? "active-button" : "ghost-button"}
              style={{ flex: 1, padding: "10px 8px" }}
            >
              {SLOT_LABELS[slot]}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export function SceneLayerPlacementControls({
  card,
  activeSlot,
  onMoveLayer,
  onScaleLayer,
  onClearLayer,
  disabled = false,
}: Omit<SceneLayerControlsProps, "onSelectSlot">) {
  const activeLayer = getSceneLayer(card, activeSlot);
  const canAdjust = !disabled && Boolean(activeLayer.spriteId);
  const minScale = SCENE_SCALE_STEPS[0];
  const maxScale = SCENE_SCALE_STEPS[SCENE_SCALE_STEPS.length - 1];

  return (
    <div className="control-stack">
      <div className="control-section">
        <div className="control-label-row">
          <span className="control-label">Placement</span>
          <span className="control-helper">X {activeLayer.offsetX} · Y {activeLayer.offsetY}</span>
        </div>
        <div className="layer-move-pad">
          <button type="button" disabled={!canAdjust} onClick={() => onMoveLayer(activeSlot, 0, -1)} className="ghost-button">↑</button>
          <button type="button" disabled={!canAdjust} onClick={() => onMoveLayer(activeSlot, -1, 0)} className="ghost-button">←</button>
          <button type="button" disabled={!canAdjust} onClick={() => onClearLayer(activeSlot)} className="ghost-button">Clear</button>
          <button type="button" disabled={!canAdjust} onClick={() => onMoveLayer(activeSlot, 1, 0)} className="ghost-button">→</button>
          <button type="button" disabled={!canAdjust} onClick={() => onMoveLayer(activeSlot, 0, 1)} className="ghost-button">↓</button>
        </div>
      </div>

      <div className="control-section">
        <div className="control-label-row">
          <span className="control-label">Scale</span>
          <span className="control-helper">{activeLayer.scale}x</span>
        </div>
        <div className="compact-action-row">
          <button type="button" disabled={!canAdjust || activeLayer.scale <= minScale} onClick={() => onScaleLayer(activeSlot, -1)} className="ghost-button" style={{ flex: 1 }}>
            Smaller
          </button>
          <button type="button" disabled={!canAdjust || activeLayer.scale >= maxScale} onClick={() => onScaleLayer(activeSlot, 1)} className="ghost-button" style={{ flex: 1 }}>
            Larger
          </button>
        </div>
      </div>
    </div>
  );
}
